import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Bell, BellRing, CalendarPlus, Check, ChevronUp, CircleStop, Clapperboard, Copy, Ellipsis, Eye, EyeOff, Hand, KeyRound,
  MessageCircle, PanelRightClose, Pin, PinOff, Play, Radio, Send, Server, Signal, Undo2, UserX, Volume2, VolumeX, Youtube,
} from "lucide-react";
import { upcomingLives, YOUTUBE_LIVE_URL, type Live } from "@/lib/schedule";
import { EASE, HEART, Head, Ink, datePart } from "../bits";
import { Part } from "../Page";
import { JoinButton, Locked, M, MembersPage, ReviewControls, VisBadge, joinCta, useRole, withRole } from "./Members";
import { CHAT, CHELSEA, CLASSES, FOUNDING, ME, MEMBERS, PROJECTS, can, replays, type ClassVideo, type Member, type Role } from "./sample";
import "./Live.css";

/* The club's private live class page (/blue-2/members/live), replacing
   Patreon lives. Three states a reviewer can flip between with the "Live"
   switch in the bottom review bar (or ?state=before|live|ended): the countdown
   before class, the live room (player + chat + questions), and the "that's a
   wrap" screen.
   Chelsea also gets a studio panel (OBS setup, start/end, moderation).
   Everything is local React state over sample data: nothing is sent. */

type Stage = "before" | "live" | "ended";
type Msg = { id: string; by: Member; text: string; t: string; hidden?: boolean; question?: boolean };
type Question = { id: string; by: Member; text: string; votes: number; voted?: boolean; status: "open" | "now" | "done" };
type Pinned = { by: Member; text: string };
type Reaction = "heart" | "thread" | "clap";
type Toast = (msg: string) => void;

const STAGES: { key: Stage; label: string }[] = [
  { key: "before", label: "Before" }, { key: "live", label: "Live now" }, { key: "ended", label: "Ended" },
];

/* ─── Sample data only this page uses ─── */

const [sam, jo, lee, mara, dana, priya] = MEMBERS;
const SEED_CHAT: Msg[] = CHAT.map((c, i) => ({ id: `seed${i}`, by: c.by, text: c.text, t: c.t }));
const MORE_CHAT: { by: Member; text: string }[] = [
  { by: dana, text: "pressing as we go or all at the end?" },
  { by: priya, text: "the fabric pull tonight is SO good" },
  { by: CHELSEA, text: "Press as you go, Dana! It keeps the panel flat" },
  { by: jo, text: "Cosmo is supervising the cutting mat again 😂" },
  { by: sam, text: "ok the starch-before-cutting tip 🤯" },
  { by: mara, text: "can we see the back of the panel?" },
  { by: CHELSEA, text: "Flipping it over now, Mara" },
  { by: lee, text: "squares cut! 12 of 12 ✂️" },
  { by: priya, text: "my seams are actually matching for once" },
  { by: dana, text: "watching from my sewing room in Boise 💛" },
];
const SEED_QS: Question[] = [
  { id: "q1", by: dana, text: "Which way do we press the seams on the front panel?", votes: 12, status: "open" },
  { id: "q2", by: mara, text: "Could I use canvas instead of fusible fleece?", votes: 7, status: "open" },
  { id: "q3", by: priya, text: "How long should the straps be for a shoulder tote?", votes: 4, status: "open" },
  { id: "q4", by: sam, text: "Is a fat quarter bundle enough for the front?", votes: 9, status: "done" },
];
const SEED_PIN: Pinned = { by: CHELSEA, text: "Pattern + supply list are on the class page. ¼\" seam allowance throughout!" };

/** Placeholder stream settings for the prototype; the real ones come from the streaming provider. */
const STREAM_SERVER = "rtmps://live.example.com:443/club";
const STREAM_KEY = "EXAMPLE-not-a-real-key-0000";

/* ─── Helpers ─── */

const PROJECT = PROJECTS[0];
const PROJECT_CLASSES = CLASSES.filter(c => c.projectId === PROJECT.id);
const day = (d: Date) => datePart(d, { weekday: "long", month: "long", day: "numeric" });
const shortDay = (d: Date) => datePart(d, { weekday: "short", month: "short", day: "numeric" });
/** The class video a live maps to: same date in the sample data, else the project's part by weekday. */
function classFor(l?: Live): ClassVideo {
  const match = l && CLASSES.find(c => c.date === datePart(l.start, { month: "short", day: "numeric", year: "numeric" }));
  return match ?? PROJECT_CLASSES[l && datePart(l.start, { weekday: "short" }) === "Thu" ? 1 : 0];
}
const first = (name: string) => name.split(" ")[0];
/** The most recent class that already has a replay (never tonight's, see hasReplay). */
const LAST_REPLAY = replays()[0];
/** True when a live falls on today's date (so copy can say "tonight"). */
const isToday = (d: Date) => datePart(d, { year: "numeric", month: "short", day: "numeric" }) === datePart(new Date(), { year: "numeric", month: "short", day: "numeric" });
/** "tonight" or "Tuesday, October 13". */
const when = (d: Date) => (isToday(d) ? "tonight" : day(d));
const pad = (n: number) => String(n).padStart(2, "0");

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), ms); return () => clearInterval(t); }, [ms]);
  return now;
}

function copy(text: string, what: string, toast: Toast) {
  const ok = () => toast(`${what} copied`);
  const fail = () => toast(`Couldn’t copy. Select the ${what.toLowerCase()} and copy it instead.`);
  try { navigator.clipboard ? navigator.clipboard.writeText(text).then(ok, fail) : fail(); } catch { fail(); }
}

const FILLS = ["soft", "pop", "extra", "strong"] as const;
function Avatar({ m, size = 34 }: { m: Member; size?: number }) {
  if (m.avatar) return <img className="ml-av" src={m.avatar} alt="" width={size} height={size} style={{ width: size, height: size }} />;
  const fill = FILLS[[...m.id].reduce((a, c) => a + c.charCodeAt(0), 0) % FILLS.length];
  const initials = m.name.split(" ").map(w => w[0]).join("").slice(0, 2);
  return <span className={`ml-av ml-av-${fill}`} style={{ width: size, height: size, fontSize: size * 0.38 }} aria-hidden="true">{initials}</span>;
}

/* ─── Header ─── */

function Countdown({ to }: { to: Date }) {
  const now = useNow(1000);
  const ms = Math.max(0, to.getTime() - now);
  if (ms === 0) return <p className="ml-count-now"><i className="ml-dot" aria-hidden="true" /> Starting now</p>;
  const d = Math.floor(ms / 864e5), h = Math.floor(ms / 36e5) % 24, m = Math.floor(ms / 6e4) % 60, s = Math.floor(ms / 1e3) % 60;
  const parts: [number, string][] = [...(d ? [[d, d === 1 ? "day" : "days"] as [number, string]] : []), [h, "hours"], [m, "min"], [s, "sec"]];
  const spoken = `${d ? `${d} ${d === 1 ? "day" : "days"}, ` : ""}${h} hours and ${m} minutes`;
  return <div className="ml-count">
    <span className="ml-sr">Starts in {spoken}</span>
    {parts.map(([n, label]) => <span key={label} className="ml-count-cell" aria-hidden="true"><b>{pad(n)}</b><small>{label}</small></span>)}
  </div>;
}

function Ticket({ live, klass, role, reminded, setReminded, toast }: {
  live: Live; klass: ClassVideo; role: Role; reminded: boolean; setReminded: (v: boolean) => void; toast: Toast;
}) {
  const allowed = can(role, "live");
  return <motion.div className="ml-ticket" initial={{ opacity: 0, y: 30, rotate: 6 }} animate={{ opacity: 1, y: 0, rotate: 2 }} transition={{ delay: 0.35, type: "spring", stiffness: 130, damping: 16 }}>
    <i className="ml-tape" aria-hidden="true" />
    <p className="ml-ticket-note">next club class in</p>
    <Countdown to={live.start} />
    <div className="ml-ticket-when">
      <p><b>{isToday(live.start) ? "Tonight" : shortDay(live.start)}</b> · 7 PM MT</p>
      <p>{klass.part}: {klass.title}</p>
    </div>
    <div className="ml-ticket-actions">
      {allowed && role !== "chelsea" && <>
        <button type="button" className="mb-btn mb-btn-ink" onClick={() => toast(`Added ${shortDay(live.start)}, 7 PM MT to your calendar (prototype)`)}>
          <CalendarPlus size={17} aria-hidden="true" /> Add to calendar
        </button>
        <button type="button" className="mb-btn mb-btn-line ml-remind" aria-pressed={reminded}
          onClick={() => { setReminded(!reminded); toast(reminded ? "Reminder off" : "We’ll email you an hour before class"); }}>
          {reminded ? <BellRing size={17} aria-hidden="true" /> : <Bell size={17} aria-hidden="true" />} {reminded ? "Reminder on" : "Remind me"}
        </button>
      </>}
      {role === "chelsea" && <a href="#ml-studio" className="mb-btn mb-btn-ink"><Radio size={17} aria-hidden="true" /> Open your studio</a>}
      {!allowed && <JoinButton need="members" />}
    </div>
    <svg className="ml-ticket-heart" viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={0.9} width={3.2} /></svg>
  </motion.div>;
}

function Header({ stage, live, klass, reminded, setReminded, toast }: {
  stage: Stage; live: Live; klass: ClassVideo; reminded: boolean; setReminded: (v: boolean) => void; toast: Toast;
}) {
  const role = useRole();
  const allowed = can(role, "live");
  const [hand, word] = stage === "live" ? ["come sew", "live!"] : stage === "ended" ? ["that’s a", "wrap!"] : ["sew with me", "live"];
  const lede = !allowed
    ? stage === "live" ? "A club class is on right now. Members are sewing along, asking questions in the chat, and keeping the replay."
      : stage === "ended" ? "Tonight’s class just wrapped. Members get the replay with chapters, usually within the hour."
        : "Twice a month, club members sew along with me live: ask questions in the chat, see every step up close, and keep every replay."
    : stage === "live" ? `${klass.part}: ${klass.title}. Grab your ${PROJECT.title.toLowerCase()} fabric and say hi in the chat.`
      : stage === "ended" ? (role === "chelsea" ? "Great class! The replay is processing and goes up on the class page with chapters." : "Thanks for sewing with me tonight! The replay will be ready shortly, with chapters.")
        : role === "chelsea" ? `You’re on ${isToday(live.start) ? "tonight" : day(live.start)} at 7 PM MT. Start streaming from OBS whenever you’re ready; members see you when you press Start class.`
          : `${PROJECT.title}, ${klass.part.toLowerCase()}: ${when(live.start)} at 7 PM Mountain. The room opens 15 minutes early, so come say hi.`;
  const showTicket = stage === "before";
  return <div className={`ml-head${showTicket ? "" : " is-compact"}`}>
    <div className="ml-head-copy">
      <p className="ml-kicker">
        {stage === "live" && <span className="ml-onair"><i className="ml-dot" aria-hidden="true" /> On air</span>}
        Club live class <VisBadge visibility="members" size="sm" />
      </p>
      <h1 className="ml-title" key={stage}>
        <motion.span className="ml-title-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.3, duration: 0.5, ease: EASE }}>{hand}</motion.span>
        <span className="ml-title-word" aria-label={word}>
          {word.split("").map((c, i) => <motion.span key={i} aria-hidden="true" initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.08 + i * 0.05, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
        </span>
      </h1>
      <motion.p className="ml-lede" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.45, ease: EASE }}>{lede}</motion.p>
    </div>
    {showTicket && <Ticket live={live} klass={klass} role={role} reminded={reminded} setReminded={setReminded} toast={toast} />}
  </div>;
}

/* ─── Prototype state switcher ─── */

/** Before / Live now / Ended, shown in the shared bottom review bar (not on the page). */
function StateSwitch({ stage, set }: { stage: Stage; set: (s: Stage) => void }) {
  return <ReviewControls label="Live page">
    {STAGES.map(s => <button key={s.key} type="button" aria-pressed={stage === s.key} onClick={() => set(s.key)}>{s.label}</button>)}
  </ReviewControls>;
}

/* ─── Before: what we're making + supplies ─── */

function Supplies({ items }: { items: string[] }) {
  const [got, setGot] = useState<Set<number>>(() => new Set());
  const toggle = (i: number) => setGot(g => { const n = new Set(g); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const all = got.size === items.length;
  return <div className="ml-pad">
    <i className="ml-tape ml-tape-pop" aria-hidden="true" />
    <h3 className="ml-pad-title">supply list</h3>
    <ul className="ml-pad-list">
      {items.map((s, i) => <li key={s}>
        <label className={got.has(i) ? "is-got" : undefined}>
          <input type="checkbox" checked={got.has(i)} onChange={() => toggle(i)} />
          <span className="ml-check" aria-hidden="true"><Check size={15} strokeWidth={3} /></span>
          <span>{s}</span>
        </label>
      </li>)}
    </ul>
    <p className="ml-pad-count" aria-live="polite">{all ? "All set! See you at 7 ♥" : `${got.size} of ${items.length} gathered`}</p>
    {PROJECT.pattern && <p className="ml-pad-pattern"><b>Pattern:</b> {PROJECT.pattern}</p>}
  </div>;
}

function Making({ klass, clubs, role }: { klass: ClassVideo; clubs: Live[]; role: Role }) {
  return <section className="ml-sec ml-make" aria-labelledby="ml-make-title">
    <div className="ml-wrap ml-make-grid">
      <Head id="ml-make-title" hand="what we’re" word="making" tone="blue" className="mb-headsm ml-make-head" />
      <motion.figure className="ml-polaroid" initial={{ opacity: 0, y: 30, rotate: -6 }} whileInView={{ opacity: 1, y: 0, rotate: -3 }} viewport={{ once: true, margin: "-60px 0px" }} transition={{ type: "spring", stiffness: 120, damping: 16 }}>
        <i className="ml-tape" aria-hidden="true" />
        <img src={PROJECT.image} alt={`Chelsea with fabric for the ${PROJECT.title}`} />
        <figcaption>{PROJECT.month.split(" ")[0]}’s project ♥</figcaption>
      </motion.figure>
      <div className="ml-make-copy">
        <p className="ml-label">{PROJECT.month} · {PROJECT.skill}</p>
        <h3 className="ml-proj-title">{PROJECT.title}</h3>
        <p className="ml-proj-blurb">{PROJECT.blurb}</p>
        <ol className="ml-plan" aria-label="Class nights">
          {PROJECT_CLASSES.map((c, i) => {
            const l = clubs.find(x => classFor(x).id === c.id);
            const next = c.id === klass.id;
            return <li key={c.id} className={next ? "is-next" : undefined}>
              <span className="ml-plan-date"><small>{l ? datePart(l.start, { weekday: "short" }) : c.date.split(",")[0].split(" ")[0]}</small><b>{l ? datePart(l.start, { day: "numeric" }) : c.date.split(" ")[1].replace(",", "")}</b></span>
              <span className="ml-plan-text"><small>Class {i + 1} · 7 PM MT</small>{c.title}</span>
              {next && <em>next up</em>}
            </li>;
          })}
        </ol>
        <Link to={withRole(M.klass(klass.id), role)} className="ml-textlink">Open the class page <ArrowRight size={16} aria-hidden="true" /></Link>
      </div>
      <Supplies items={PROJECT.supplies} />
    </div>
  </section>;
}

/* ─── Before: how the room works, drop a question early, last class ─── */

const ROOM_HOW: { icon: ReactNode; title: string; text: string }[] = [
  { icon: <MessageCircle size={22} aria-hidden="true" />, title: "Say hi in the chat",
    text: "The room opens 15 minutes early. The chat is members only, so show off your fabric pull and cheer each other on." },
  { icon: <Hand size={22} aria-hidden="true" />, title: "Ask, then vote ▲",
    text: "Raise a question anytime. Tap ▲ on the ones you’re wondering too; I answer the top ones at the break and at the end." },
  { icon: <Clapperboard size={22} aria-hidden="true" />, title: "Replay within the hour",
    text: "Missed a step? The replay lands on the class page with chapters, usually within an hour of the wrap." },
];

/** Early questions: members add to the same queue the live room's Questions tab shows. */
function EarlyQuestions({ qs, setQs, chelsea, toast }: { qs: Question[]; setQs: (f: (q: Question[]) => Question[]) => void; chelsea: boolean; toast: Toast }) {
  const [text, setText] = useState("");
  const open = qs.filter(q => q.status === "open").sort((a, b) => b.votes - a.votes);
  const mine = open.filter(q => q.by.id === ME.id);
  const shown = [...mine, ...open.filter(q => q.by.id !== ME.id)].slice(0, chelsea ? 5 : 3);
  const add = (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setQs(q => [...q, { id: `q${Date.now()}`, by: ME, text: t, votes: 1, voted: true, status: "open" }]);
    setText("");
    toast("Added to Chelsea’s question queue for tonight");
  };
  const vote = (id: string) => setQs(list => list.map(x => x.id === id ? { ...x, voted: !x.voted, votes: x.votes + (x.voted ? -1 : 1) } : x));
  return <div className="ml-early">
    <i className="ml-tape ml-tape-pop" aria-hidden="true" />
    <p className="ml-note">{chelsea ? "already in your queue" : "drop your question early"}</p>
    <h3 className="ml-early-title" id="ml-early-title">{chelsea ? `${open.length} questions waiting` : "Got a question already?"}</h3>
    <p className="ml-early-text">{chelsea
      ? "Members can ask before class starts. These open the Questions tab when you go live, top votes first."
      : "Add it now and it’s waiting in the Questions tab when class starts. Other members can vote it up."}</p>
    {!chelsea && <form className="ml-early-form" onSubmit={add}>
      <label className="mb-sr" htmlFor="ml-early-input">Your question for Chelsea</label>
      <textarea id="ml-early-input" rows={2} value={text} maxLength={240} placeholder="e.g. Can I use canvas instead of fusible fleece?"
        onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.form?.requestSubmit(); } }} />
      <button type="submit" className="mb-btn mb-btn-ink" disabled={!text.trim()}><Send size={16} aria-hidden="true" /> Add to the queue</button>
    </form>}
    <ol className="ml-early-list" aria-label="Questions in the queue" aria-live="polite">
      {shown.map(q => <li key={q.id} className={q.by.id === ME.id ? "is-mine" : undefined}>
        <button type="button" className="ml-vote" aria-pressed={!!q.voted} disabled={chelsea}
          aria-label={`${q.voted ? "Remove your vote" : "Me too"}: ${q.votes} ${q.votes === 1 ? "vote" : "votes"}`} onClick={() => vote(q.id)}>
          <ChevronUp size={16} strokeWidth={3} aria-hidden="true" /><span>{q.votes}</span>
        </button>
        <div><p>{q.text}</p><small>{q.by.id === ME.id ? "You" : q.by.name}</small></div>
      </li>)}
    </ol>
    {open.length > shown.length && <p className="ml-early-more">+ {open.length - shown.length} more in the queue</p>}
  </div>;
}

/** The last class with a replay, for catching up before tonight. */
function LastReplay({ role }: { role: Role }) {
  const c = LAST_REPLAY;
  if (!c) return null;
  const project = PROJECTS.find(p => p.id === c.projectId);
  const pct = role === "chelsea" ? undefined : c.watched;
  return <Link to={withRole(M.klass(c.id), role)} className="ml-last">
    <span className="ml-last-img">
      <img src={c.poster} alt="" />
      <span className="ml-last-play" aria-hidden="true"><Play size={22} fill="currentColor" /></span>
      <span className="ml-last-len">{c.length}</span>
    </span>
    <span className="ml-last-body">
      <span className="ml-label">Last class · replay</span>
      <b className="ml-last-title">{c.title}</b>
      <span className="ml-last-meta">{project?.title} · {c.part} · {c.chapters.length} chapters</span>
      {pct !== undefined && pct < 1 && <span className="ml-last-prog"><span className="ml-last-bar"><i style={{ width: `${Math.round(pct * 100)}%` }} /></span>{Math.round(pct * 100)}% watched</span>}
      <span className="ml-last-go">{pct !== undefined && pct > 0 && pct < 1 ? "Pick up where you left off" : "Watch the replay"} <ArrowRight size={16} aria-hidden="true" /></span>
    </span>
  </Link>;
}

function Ready({ role, qs, setQs, toast }: { role: Role; qs: Question[]; setQs: (f: (q: Question[]) => Question[]) => void; toast: Toast }) {
  const chelsea = role === "chelsea";
  return <section className="ml-sec ml-ready" aria-labelledby="ml-ready-title">
    <div className="ml-wrap">
      <Head id="ml-ready-title" hand={chelsea ? "before you" : "how the"} word={chelsea ? "go live" : "room works"} tone="blue" className="mb-headsm" />
      {!chelsea && <ol className="ml-how">
        {ROOM_HOW.map((h, i) => <li key={h.title} style={{ rotate: `${[-1.2, 0.8, -0.6][i]}deg` }}>
          <span className="ml-how-icon">{h.icon}</span>
          <h3>{h.title}</h3>
          <p>{h.text}</p>
        </li>)}
      </ol>}
      <div className="ml-ready-grid">
        <EarlyQuestions qs={qs} setQs={setQs} chelsea={chelsea} toast={toast} />
        <div className="ml-ready-side">
          <p className="ml-note">{chelsea ? "last class’s replay" : "catch up first?"}</p>
          <LastReplay role={role} />
        </div>
      </div>
    </div>
  </section>;
}

/* ─── Schedule + free YouTube teaser ─── */

function Schedule({ role, stage, current }: { role: Role; stage: Stage; current: Live }) {
  // Once tonight's class has ended (prototype state), it drops off the list; while it's on, it's marked live.
  const same = (l: Live) => l.start.getTime() === current.start.getTime();
  const lives = upcomingLives(5).filter(l => !(stage === "ended" && same(l))).slice(0, 4);
  const yt = upcomingLives(1, "youtube")[0];
  return <section className="ml-sec ml-sched" aria-labelledby="ml-sched-title">
    <div className="ml-wrap ml-sched-grid">
      <div>
        <p className="ml-label ml-sched-kicker">Can’t wait for class?</p>
        <Head id="ml-sched-title" hand="free on" word="YouTube" tone="pink" className="mb-headsm" />
        <p className="ml-sched-text">Club classes are the second Tuesday of the month and the Thursday after. Most other Tuesdays I’m live on YouTube, free for everyone, at 7 PM Mountain.
          {yt && <> Next one: <b>{day(yt.start)}</b>.</>}</p>
        <a href={YOUTUBE_LIVE_URL} target="_blank" rel="noreferrer" className="mb-btn ml-btn-live"><Youtube size={18} aria-hidden="true" /> Watch free on YouTube</a>
      </div>
      <div className="ml-dates-wrap">
        <p className="ml-dates-note">coming up</p>
        <ul className="ml-dates">
          {lives.map((l, i) => {
            const club = l.kind === "club";
            const onNow = stage === "live" && same(l);
            const inner = <>
              <small>{datePart(l.start, { weekday: "short" })}</small>
              <b>{datePart(l.start, { day: "numeric" })}</b>
              <span>{datePart(l.start, { month: "short" })} · 7 PM</span>
              {onNow ? <em><i className="ml-dot" aria-hidden="true" /> Live now</em>
                : club ? <VisBadge visibility="members" size="sm" className="ml-date-vis" />
                  : <em><Play size={10} fill="currentColor" aria-hidden="true" /> YouTube</em>}
            </>;
            const label = `${day(l.start)}, 7 PM Mountain: ${club ? `club class for members${onNow ? ", live now" : ""}` : "free live on YouTube"}`;
            return <li key={l.start.toISOString()} className={`ml-date-${l.kind}${onNow ? " is-now" : ""}`} style={{ rotate: `${[-2.5, 2, -1, 2.5, -1.5][i]}deg` }}>
              {club
                ? (can(role, "live") ? <div className="ml-date" aria-label={label} role="group">{inner}</div>
                  : <Link className="ml-date" to={joinCta(role, "members")?.to ?? withRole(M.join, role)} aria-label={`${label}. ${joinCta(role, "members")?.short ?? "Join the club"} to watch.`}>{inner}</Link>)
                : <a className="ml-date" href={l.url} target="_blank" rel="noreferrer" aria-label={label}>{inner}</a>}
            </li>;
          })}
        </ul>
      </div>
    </div>
  </section>;
}

/* ─── Live room ─── */

function Floaters({ items }: { items: { id: number; kind: Reaction; x: number }[] }) {
  return <div className="ml-floaters" aria-hidden="true">
    <AnimatePresence>
      {items.map(f => <motion.span key={f.id} className={`ml-float ml-float-${f.kind}`} initial={{ y: 0, x: 0, opacity: 0, scale: 0.6 }}
        animate={{ y: -240, x: f.x, opacity: [0, 1, 1, 0], scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 2.1, ease: "easeOut" }}>
        {f.kind === "heart" ? <svg viewBox="0 0 30 46"><path d={HEART} /></svg> : f.kind === "thread" ? "🧵" : "👏"}
      </motion.span>)}
    </AnimatePresence>
  </div>;
}

function Message({ m, chelsea, open, setOpen, onPin, onHide, onMute, muted }: {
  m: Msg; chelsea: boolean; open: boolean; setOpen: (id: string | null) => void; muted: boolean;
  onPin: () => void; onHide: () => void; onMute: () => void;
}) {
  const host = m.by.badge === "chelsea";
  const mine = m.by.id === ME.id;
  return <li className={`ml-msg${host ? " is-host" : ""}${mine ? " is-mine" : ""}${m.question ? " is-q" : ""}`}>
    <Avatar m={m.by} size={32} />
    <div className="ml-msg-body">
      <p className="ml-msg-meta">
        <b>{m.by.name}</b>
        {host && <span className="ml-badge ml-badge-host">Host</span>}
        {m.by.badge === "founding" && <span className="ml-badge">{FOUNDING}</span>}
        {mine && <span className="ml-badge">You</span>}
        {m.question && <span className="ml-badge ml-badge-q"><Hand size={10} aria-hidden="true" /> Question</span>}
        {muted && <span className="ml-badge ml-badge-muted">Muted</span>}
        <time>{m.t}</time>
      </p>
      <p className="ml-msg-text">{m.text}</p>
      {chelsea && open && <div className="ml-mod" role="group" aria-label={`Moderate ${m.by.name}’s message`}>
        <button type="button" onClick={onPin}><Pin size={14} aria-hidden="true" /> Pin</button>
        {!host && <button type="button" onClick={onHide}><EyeOff size={14} aria-hidden="true" /> Hide</button>}
        {!host && !mine && <button type="button" onClick={onMute} disabled={muted}><UserX size={14} aria-hidden="true" /> {muted ? "Muted" : `Mute ${first(m.by.name)}`}</button>}
      </div>}
    </div>
    {chelsea && <button type="button" className="ml-mod-toggle" aria-expanded={open} aria-label={`Moderation options for ${m.by.name}’s message`} onClick={() => setOpen(open ? null : m.id)}>
      <Ellipsis size={16} aria-hidden="true" />
    </button>}
  </li>;
}

type RoomState = {
  msgs: Msg[]; setMsgs: (f: (m: Msg[]) => Msg[]) => void;
  qs: Question[]; setQs: (f: (q: Question[]) => Question[]) => void;
  pinned: Pinned | null; setPinned: (p: Pinned | null) => void;
  muted: string[]; setMuted: (f: (m: string[]) => string[]) => void;
  viewers: number; startedAt: number; toast: Toast;
};

function clockAt(startedAt: number) {
  const mins = Math.max(0, Math.floor((Date.now() - startedAt) / 6e4));
  return `${7 + Math.floor(mins / 60)}:${pad(mins % 60)}`;
}

function ChatPanel({ room, chelsea, onClose, id }: { room: RoomState; chelsea: boolean; onClose: () => void; id: string }) {
  const { msgs, setMsgs, qs, setQs, pinned, setPinned, muted, setMuted, startedAt, toast } = room;
  const [tab, setTab] = useState<"chat" | "qs">("chat");
  const [text, setText] = useState("");
  const [asQ, setAsQ] = useState(false);
  const [pinIt, setPinIt] = useState(false);
  const [modOpen, setModOpen] = useState<string | null>(null);
  const logRef = useRef<HTMLOListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const me = chelsea ? CHELSEA : ME;

  // Follow new messages unless the reader has scrolled up to read back.
  const nearBottom = useRef(true);
  useEffect(() => {
    const el = logRef.current;
    if (el && nearBottom.current) el.scrollTo({ top: el.scrollHeight });
  }, [msgs.length, tab]);

  const send = (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    const question = !chelsea && (asQ || tab === "qs");
    setMsgs(ms => [...ms, { id: `me${Date.now()}`, by: me, text: t, t: clockAt(startedAt), question }]);
    if (question) {
      setQs(q => [...q, { id: `q${Date.now()}`, by: me, text: t, votes: 1, voted: true, status: "open" }]);
      toast("Question added to Chelsea’s queue");
      setAsQ(false);
    }
    if (chelsea && pinIt) { setPinned({ by: me, text: t }); setPinIt(false); toast("Pinned to the top of the chat"); }
    nearBottom.current = true;
    setText("");
  };
  const tabs: { key: "chat" | "qs"; label: string }[] = [{ key: "chat", label: "Chat" }, { key: "qs", label: "Questions" }];
  const onTabKey = (e: KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const next = tab === "chat" ? "qs" : "chat";
    setTab(next);
    document.getElementById(`${id}-tab-${next}`)?.focus();
  };
  const openQs = qs.filter(q => q.status !== "done").length;
  const order = { now: 0, open: 1, done: 2 } as const;
  const sortedQs = [...qs].sort((a, b) => order[a.status] - order[b.status] || b.votes - a.votes);
  const visible = msgs.filter(m => chelsea || !m.hidden);
  const answer = (qid: string, status: Question["status"]) => setQs(q => q.map(x => x.id === qid ? { ...x, status } : status === "now" && x.status === "now" ? { ...x, status: "done" } : x));

  return <aside className="ml-chat" aria-label="Live chat and questions">
    <div className="ml-chat-head">
      <div className="ml-chat-tabs" role="tablist" aria-label="Chat panel" onKeyDown={onTabKey}>
        {tabs.map(t => <button key={t.key} type="button" role="tab" id={`${id}-tab-${t.key}`} aria-selected={tab === t.key} aria-controls={`${id}-pane-${t.key}`} tabIndex={tab === t.key ? 0 : -1} onClick={() => setTab(t.key)}>
          {t.label}{t.key === "qs" && <span className="ml-pill">{openQs}</span>}
        </button>)}
      </div>
      <button type="button" className="ml-icon-btn" onClick={onClose} aria-label="Hide chat"><PanelRightClose size={18} aria-hidden="true" /></button>
    </div>

    {pinned && <div className="ml-pinned">
      <Pin size={15} aria-hidden="true" />
      <p><b>Pinned by Chelsea{pinned.by.id !== CHELSEA.id && <> · from {pinned.by.name}</>}</b>{pinned.text}</p>
      {chelsea && <button type="button" className="ml-icon-btn" onClick={() => setPinned(null)} aria-label="Unpin message"><PinOff size={15} aria-hidden="true" /></button>}
    </div>}

    {tab === "chat" ? <div className="ml-pane" role="tabpanel" id={`${id}-pane-chat`} aria-labelledby={`${id}-tab-chat`}>
      <ol className="ml-log" ref={logRef} role="log" aria-label="Chat messages"
        onScroll={e => { const el = e.currentTarget; nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; }}>
        <li className="ml-log-start">Chat for members only. Be kind, ask anything ♥</li>
        {visible.map(m => m.hidden
          ? <li key={m.id} className="ml-msg-hidden"><EyeOff size={13} aria-hidden="true" /> You hid {m.by.name}’s message.
            <button type="button" onClick={() => setMsgs(ms => ms.map(x => x.id === m.id ? { ...x, hidden: false } : x))}><Undo2 size={13} aria-hidden="true" /> Undo</button></li>
          : <Message key={m.id} m={m} chelsea={chelsea} open={modOpen === m.id} setOpen={setModOpen} muted={muted.includes(m.by.id)}
            onPin={() => { setPinned({ by: m.by, text: m.text }); setModOpen(null); toast("Pinned to the top of the chat"); }}
            onHide={() => { setMsgs(ms => ms.map(x => x.id === m.id ? { ...x, hidden: true } : x)); setModOpen(null); }}
            onMute={() => { setMuted(u => [...u, m.by.id]); setModOpen(null); toast(`${m.by.name} is muted for this class`); }} />)}
      </ol>
    </div> : <div className="ml-pane" role="tabpanel" id={`${id}-pane-qs`} aria-labelledby={`${id}-tab-qs`}>
      <div className="ml-qs">
        <p className="ml-qs-note">{chelsea ? "Top questions float up. Answer one on stream and it shows on screen." : "Chelsea answers the top questions at the break and at the end. Tap ▲ if you’re wondering too."}</p>
        <ol className="ml-q-list">
          {sortedQs.map(q => <li key={q.id} className={`ml-q is-${q.status}`}>
            <button type="button" className="ml-vote" aria-pressed={!!q.voted} disabled={q.status === "done"}
              aria-label={`${q.voted ? "Remove your vote" : "Me too"}: ${q.votes} ${q.votes === 1 ? "vote" : "votes"}`}
              onClick={() => setQs(list => list.map(x => x.id === q.id ? { ...x, voted: !x.voted, votes: x.votes + (x.voted ? -1 : 1) } : x))}>
              <ChevronUp size={16} strokeWidth={3} aria-hidden="true" /><span>{q.votes}</span>
            </button>
            <div className="ml-q-body">
              <p>{q.text}</p>
              <small>{q.by.name}{q.status === "now" ? " · answering now" : q.status === "done" ? " · answered" : ""}</small>
              {chelsea && q.status !== "done" && <div className="ml-q-actions">
                {q.status === "open" && <button type="button" onClick={() => answer(q.id, "now")}><Radio size={13} aria-hidden="true" /> Answer on stream</button>}
                <button type="button" onClick={() => answer(q.id, "done")}><Check size={13} aria-hidden="true" /> Mark answered</button>
              </div>}
            </div>
            {q.status === "done" && <Check className="ml-q-done" size={16} aria-label="Answered" />}
          </li>)}
        </ol>
      </div>
    </div>}

    <form className="ml-composer" onSubmit={send}>
      <div className="ml-composer-row">
        <label className="ml-sr" htmlFor={`${id}-input`}>{tab === "qs" || asQ ? "Your question for Chelsea" : "Chat message"}</label>
        <input id={`${id}-input`} ref={inputRef} value={text} onChange={e => setText(e.target.value)} maxLength={240} autoComplete="off"
          placeholder={chelsea ? "Message the class…" : tab === "qs" || asQ ? "Ask Chelsea a question…" : "Say hi, cheer, ask…"} />
        <button type="submit" className="ml-send" disabled={!text.trim()} aria-label={tab === "qs" || asQ ? "Ask question" : "Send message"}><Send size={17} aria-hidden="true" /></button>
      </div>
      <div className="ml-composer-foot">
        {chelsea
          ? <button type="button" className="ml-chip" aria-pressed={pinIt} onClick={() => setPinIt(!pinIt)}><Pin size={13} aria-hidden="true" /> Pin when sent</button>
          : tab === "chat" && <button type="button" className="ml-chip" aria-pressed={asQ} onClick={() => { setAsQ(!asQ); inputRef.current?.focus(); }}><Hand size={13} aria-hidden="true" /> Raise as a question</button>}
        <span>as <b>{me.name}</b></span>
      </div>
    </form>
  </aside>;
}

function OnAirBar({ room, onEnd }: { room: RoomState; onEnd: () => void }) {
  const now = useNow(1000);
  const [confirm, setConfirm] = useState(false);
  const s = Math.max(0, Math.floor((now - room.startedAt) / 1000));
  return <div className="ml-airbar" role="group" aria-label="Your class controls">
    <span className="ml-airbar-live"><i className="ml-dot" aria-hidden="true" /> You’re live <span aria-hidden="true">· {Math.floor(s / 3600)}:{pad(Math.floor(s / 60) % 60)}:{pad(s % 60)}</span></span>
    <span className="ml-airbar-meta">{room.viewers} watching · {room.qs.filter(q => q.status !== "done").length} questions in the queue</span>
    <a href="#ml-studio" className="ml-airbar-link">Studio &amp; OBS</a>
    {confirm
      ? <span className="ml-airbar-confirm">End for everyone?
        <button type="button" className="mb-btn mb-btn-sm ml-btn-go" onClick={onEnd}>End class</button>
        <button type="button" className="mb-btn mb-btn-sm mb-btn-line" onClick={() => setConfirm(false)}>Keep going</button></span>
      : <button type="button" className="mb-btn mb-btn-sm ml-btn-stop" onClick={() => setConfirm(true)}><CircleStop size={16} aria-hidden="true" /> End class</button>}
  </div>;
}

function LiveRoom({ room, klass, chelsea, role, reactions, react, floaters, onEnd }: {
  room: RoomState; klass: ClassVideo; chelsea: boolean; role: Role; onEnd: () => void;
  reactions: Record<Reaction, number>; react: (k: Reaction) => void; floaters: { id: number; kind: Reaction; x: number }[];
}) {
  const [muted, setMuted] = useState(true);
  const [chatOpen, setChatOpen] = useState(true);
  const [seen, setSeen] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduced = useReducedMotion();
  const answering = room.qs.find(q => q.status === "now");
  const unread = chatOpen ? 0 : room.msgs.filter(m => !m.hidden).length - seen;
  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted]);
  // Reduced motion: keep the stream still until the viewer presses play.
  useEffect(() => { if (reduced) videoRef.current?.pause(); }, [reduced]);
  const [paused, setPaused] = useState(false);

  const close = () => { setSeen(room.msgs.filter(m => !m.hidden).length); setChatOpen(false); };
  const REACT: { k: Reaction; label: string; glyph: ReactNode }[] = [
    { k: "heart", label: "Send a heart", glyph: <svg viewBox="0 0 30 46" aria-hidden="true"><path d={HEART} /></svg> },
    { k: "thread", label: "Send a thread", glyph: <span aria-hidden="true">🧵</span> },
    { k: "clap", label: "Send applause", glyph: <span aria-hidden="true">👏</span> },
  ];
  return <section className="ml-sec ml-room" aria-labelledby="ml-room-title">
    {chelsea && <div className="ml-wrap ml-airbar-wrap"><OnAirBar room={room} onEnd={onEnd} /></div>}
    <div className={`ml-wrap ml-room-grid${chatOpen ? "" : " is-theater"}`}>
      <div className="ml-player-col">
        <div className="ml-player">
          <video ref={videoRef} src="/video/live-class.mp4" poster="/video/live-class.jpg" autoPlay={!reduced} muted loop playsInline
            onPlay={() => setPaused(false)} onPause={() => setPaused(true)} aria-label={`Live class video: ${klass.title} (sample footage)`} />
          <div className="ml-player-top">
            <span className="ml-live-badge"><i className="ml-dot" aria-hidden="true" /> Live</span>
            <span className="ml-viewers"><Eye size={14} aria-hidden="true" /> {room.viewers} <span className="ml-hide-sm">watching</span><span className="ml-sr"> watching</span></span>
          </div>
          <AnimatePresence>
            {answering && <motion.div className="ml-lower" key={answering.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}>
              <small><Hand size={12} aria-hidden="true" /> Answering {first(answering.by.name)}’s question</small>
              <p>{answering.text}</p>
            </motion.div>}
          </AnimatePresence>
          {!reduced && <Floaters items={floaters} />}
          <div className="ml-player-ctrls">
            {paused && <button type="button" className="ml-ctrl" onClick={() => videoRef.current?.play()} aria-label="Play stream"><Play size={18} fill="currentColor" aria-hidden="true" /></button>}
            <button type="button" className="ml-ctrl" onClick={() => setMuted(!muted)} aria-pressed={!muted} aria-label={muted ? "Turn sound on" : "Mute"}>
              {muted ? <VolumeX size={18} aria-hidden="true" /> : <Volume2 size={18} aria-hidden="true" />}
              {muted && <span className="ml-ctrl-text">Tap for sound</span>}
            </button>
          </div>
        </div>
        <div className="ml-info">
          <div className="ml-info-copy">
            <p className="ml-label ml-room-kicker">{PROJECT.title} · {klass.part}</p>
            <h2 id="ml-room-title" className="ml-room-title">{klass.title}</h2>
            <p className="ml-host"><Avatar m={CHELSEA} size={26} /> with Chelsea {chelsea && <span className="ml-badge ml-badge-host">You</span>}</p>
          </div>
          <div className="ml-reacts" role="group" aria-label="Reactions">
            {REACT.map(r => <button key={r.k} type="button" className={`ml-react ml-react-${r.k}`} onClick={() => react(r.k)} aria-label={`${r.label} (${reactions[r.k]} so far)`}>
              {r.glyph}<span aria-hidden="true">{reactions[r.k]}</span>
            </button>)}
            {!chatOpen && <button type="button" className="mb-btn mb-btn-pop mb-btn-sm ml-show-chat" onClick={() => setChatOpen(true)}>
              Show chat{unread > 0 && <span className="ml-pill">{unread} new</span>}
            </button>}
          </div>
        </div>
        <details className="ml-tonight">
          <summary>Tonight’s supplies &amp; notes</summary>
          <div className="ml-tonight-in">
            <ul>{PROJECT.supplies.map(s => <li key={s}>{s}</li>)}</ul>
            <p>Missed the start? The replay keeps chapters, and the pattern is on the <Link to={withRole(M.klass(klass.id), role)}>class page</Link>.</p>
          </div>
        </details>
      </div>
      {chatOpen && <ChatPanel room={room} chelsea={chelsea} onClose={close} id="ml-chat" />}
    </div>
  </section>;
}

/* ─── Ended ─── */

function Ended({ klass, next, role, room, toast }: { klass: ClassVideo; next?: Live; role: Role; room: RoomState; toast: Toast }) {
  const reduced = useReducedMotion();
  const chelsea = role === "chelsea";
  const [pct, setPct] = useState(18);
  useEffect(() => {
    const t = setInterval(() => setPct(p => (p >= 92 ? p : p + 3)), 900);
    return () => clearInterval(t);
  }, []);
  const nextClass = next && classFor(next);
  const answered = room.qs.filter(q => q.status === "done").length;
  const stats = [
    { n: room.viewers, label: "sewed along" },
    { n: room.msgs.filter(m => !m.hidden).length + 80, label: "chat messages" },
    { n: answered, label: answered === 1 ? "question answered" : "questions answered" },
  ];
  return <section className="ml-sec ml-ended" aria-labelledby="ml-ended-title">
    <div className="ml-wrap ml-ended-grid">
      <motion.figure className="ml-polaroid ml-replay" initial={{ opacity: 0, y: 30, rotate: 6 }} animate={{ opacity: 1, y: 0, rotate: 2.5 }} transition={{ type: "spring", stiffness: 120, damping: 16 }}>
        <i className="ml-tape ml-tape-soft" aria-hidden="true" />
        <div className="ml-replay-img">
          <img src={klass.poster} alt={`Still from tonight’s class: ${klass.title}`} />
          <span className="ml-replay-chip"><span className={reduced ? "" : "ml-spin"} aria-hidden="true">✂️</span> Replay processing</span>
        </div>
        <figcaption>{klass.part} · {klass.date}</figcaption>
      </motion.figure>
      <div className="ml-ended-copy">
        <p className="ml-note">{chelsea ? "great class, Chelsea!" : "thanks for sewing with me!"}</p>
        <h2 id="ml-ended-title" className="ml-ended-title">Replay will be ready shortly</h2>
        <p className="ml-ended-text">{chelsea
          ? "It usually takes under an hour. It lands on the class page with chapters, and the chat and questions are saved alongside it. Members get a note when it’s up."
          : "It usually takes under an hour. It lands on the class page with chapters, and the chat and questions are saved alongside it. We’ll send you a note when it’s up."}</p>
        <div className="ml-progress">
          <div className="ml-progress-bar" role="progressbar" aria-label="Replay processing" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}><i style={{ width: `${pct}%` }} /></div>
          <span aria-hidden="true">{pct}%</span>
        </div>
        <div className="ml-ended-actions">
          <Link to={withRole(M.klass(klass.id), role)} className="mb-btn mb-btn-ink">Go to the class page <ArrowRight size={17} aria-hidden="true" /></Link>
          {chelsea
            ? <Link to={withRole(M.posts, role)} className="mb-btn mb-btn-line">Post a thank-you to members</Link>
            : <Link to={withRole(M.community, role)} className="mb-btn mb-btn-line">Share your progress in Makes</Link>}
        </div>
        <ul className="ml-stats" aria-label="Tonight’s class">
          {stats.map((s, i) => <li key={s.label} style={{ rotate: `${[-2, 1.5, -1][i]}deg` }}><b>{s.n}</b><span>{s.label}</span></li>)}
        </ul>
        {next && nextClass && <div className="ml-next">
          <span className="ml-plan-date"><small>{datePart(next.start, { weekday: "short" })}</small><b>{datePart(next.start, { day: "numeric" })}</b></span>
          <div><small>Next club class · 7 PM MT</small><p>{nextClass.part}: {nextClass.title}</p></div>
          <button type="button" className="ml-icon-btn ml-next-cal" onClick={() => toast(`Added ${shortDay(next.start)}, 7 PM MT to your calendar (prototype)`)} aria-label={`Add ${shortDay(next.start)} to your calendar`}><CalendarPlus size={18} aria-hidden="true" /></button>
        </div>}
      </div>
    </div>
  </section>;
}

/* ─── Chelsea's studio ─── */

function Studio({ stage, startedAt, onStart, onEnd, onReset, room, toast }: {
  stage: Stage; startedAt: number; onStart: () => void; onEnd: () => void; onReset: () => void; room: RoomState; toast: Toast;
}) {
  const [obs, setObs] = useState<"waiting" | "connected">(stage === "before" ? "waiting" : "connected");
  const [reveal, setReveal] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const now = useNow(1000);
  const reduced = useReducedMotion();
  useEffect(() => {
    setConfirm(false);
    if (stage !== "before") return;
    setObs("waiting");
    const t = setTimeout(() => setObs("connected"), 2600);
    return () => clearTimeout(t);
  }, [stage]);
  const onAir = Math.max(0, Math.floor((now - startedAt) / 1000));
  const timer = `${Math.floor(onAir / 3600)}:${pad(Math.floor(onAir / 60) % 60)}:${pad(onAir % 60)}`;
  const status = stage === "live" ? { k: "live", label: `Live · ${timer}` } : stage === "ended" ? { k: "off", label: "Class ended" }
    : obs === "connected" ? { k: "ready", label: "OBS connected · preview" } : { k: "wait", label: "Waiting for OBS" };
  const hidden = room.msgs.filter(m => m.hidden).length;
  const mutedMembers = MEMBERS.filter(m => room.muted.includes(m.id));

  return <section className="ml-studio" id="ml-studio" aria-labelledby="ml-studio-title">
    <div className="ml-wrap">
      <div className="ml-studio-head">
        <div>
          <p className="ml-label ml-studio-kicker">Only you see this</p>
          <h2 id="ml-studio-title" className="ml-studio-title">Your studio</h2>
        </div>
        <p className={`ml-status ml-status-${status.k}`}><i aria-hidden="true" /><span aria-hidden="true">{status.label}</span>
          <span className="ml-sr" role="status">{status.k === "live" ? "Live" : status.label}</span></p>
      </div>
      <div className="ml-studio-grid">
        <div className="ml-desk">
          <div className={`ml-monitor is-${status.k}`}>
            {status.k === "wait" ? <div className="ml-monitor-wait"><Signal size={28} aria-hidden="true" /><p>Waiting for a signal from OBS…</p><small>Press <b>Start Streaming</b> in OBS</small></div>
              : status.k === "off" ? <><img src="/video/live-class.jpg" alt="" /><span className="ml-monitor-tag">Off air</span></>
                : <><video src="/video/live-class.mp4" poster="/video/live-class.jpg" autoPlay={!reduced} muted loop playsInline aria-label="Your stream preview (sample footage)" />
                  <span className={`ml-monitor-tag${status.k === "live" ? " is-live" : ""}`}>{status.k === "live" ? "On air" : "Preview · members can’t see this yet"}</span></>}
          </div>
          <dl className="ml-health">
            <div><dt>Video</dt><dd>{status.k === "wait" || status.k === "off" ? "—" : "1080p · 30 fps"}</dd></div>
            <div><dt>Bitrate</dt><dd>{status.k === "wait" || status.k === "off" ? "—" : "6.0 Mbps"}</dd></div>
            <div><dt>Health</dt><dd>{status.k === "wait" ? "No signal" : status.k === "off" ? "—" : "Excellent"}</dd></div>
            <div><dt>Watching</dt><dd>{stage === "live" ? room.viewers : stage === "ended" ? `${room.viewers} at peak` : "—"}</dd></div>
          </dl>
          <div className="ml-desk-actions">
            {stage === "before" && <>
              <button type="button" className="mb-btn ml-btn-go" onClick={onStart} disabled={obs !== "connected"}><Radio size={18} aria-hidden="true" /> Start class</button>
              <p className="ml-desk-hint">{obs === "connected" ? "Members see you the moment you press Start." : "Start streaming in OBS first; the preview shows up here."}</p>
            </>}
            {stage === "live" && (confirm
              ? <div className="ml-confirm" role="group" aria-label="Confirm ending the class">
                <p>End the class for everyone? The replay starts processing.</p>
                <button type="button" className="mb-btn ml-btn-go" onClick={onEnd}><CircleStop size={17} aria-hidden="true" /> End class</button>
                <button type="button" className="mb-btn mb-btn-line" onClick={() => setConfirm(false)}>Keep streaming</button>
              </div>
              : <><button type="button" className="mb-btn ml-btn-stop" onClick={() => setConfirm(true)}><CircleStop size={18} aria-hidden="true" /> End class</button>
                <p className="ml-desk-hint">Then stop streaming in OBS.</p></>)}
            {stage === "ended" && <>
              <button type="button" className="mb-btn mb-btn-line" onClick={onReset}>Set up the next class</button>
              <p className="ml-desk-hint">The replay goes to the class page when it’s ready.</p>
            </>}
          </div>
          {(hidden > 0 || mutedMembers.length > 0) && <div className="ml-modsum">
            <p className="ml-label ml-studio-kicker">Moderation</p>
            {hidden > 0 && <p>{hidden} hidden {hidden === 1 ? "message" : "messages"}</p>}
            <ul>{mutedMembers.map(m => <li key={m.id}><UserX size={14} aria-hidden="true" /> {m.name} is muted
              <button type="button" onClick={() => { room.setMuted(u => u.filter(x => x !== m.id)); toast(`${m.name} can chat again`); }}>Unmute</button></li>)}</ul>
          </div>}
        </div>

        <div className="ml-obs">
          <h3 className="ml-obs-title">Stream from OBS</h3>
          <ol className="ml-steps">
            <li>In OBS, open <b>Settings → Stream</b> and pick <b>Service: Custom…</b></li>
            <li>Paste the server and stream key below.</li>
            <li>Under <b>Output</b>: 1080p, 30 fps, 6000 Kbps, keyframe interval 2s.</li>
            <li>Press <b>Start Streaming</b> in OBS, check the preview here, then press <b>Start class</b>.</li>
          </ol>
          <div className="ml-field">
            <span className="ml-field-label" id="ml-server-l"><Server size={14} aria-hidden="true" /> Server</span>
            <div className="ml-field-row">
              <code aria-labelledby="ml-server-l">{STREAM_SERVER.split(/(?<=\/)/).map((part, i) => <span key={i}>{i > 0 && <wbr />}{part}</span>)}</code>
              <button type="button" className="ml-icon-btn" onClick={() => copy(STREAM_SERVER, "Server URL", toast)} aria-label="Copy server URL"><Copy size={16} aria-hidden="true" /></button>
            </div>
          </div>
          <div className="ml-field">
            <span className="ml-field-label" id="ml-key-l"><KeyRound size={14} aria-hidden="true" /> Stream key</span>
            <div className="ml-field-row">
              <code aria-labelledby="ml-key-l">{reveal ? STREAM_KEY : <span aria-label="hidden">{"•".repeat(22)}</span>}</code>
              <button type="button" className="ml-icon-btn" onClick={() => setReveal(!reveal)} aria-pressed={reveal} aria-label={reveal ? "Hide stream key" : "Show stream key"}>
                {reveal ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
              </button>
              <button type="button" className="ml-icon-btn" onClick={() => copy(STREAM_KEY, "Stream key", toast)} aria-label="Copy stream key"><Copy size={16} aria-hidden="true" /></button>
            </div>
          </div>
          <p className="ml-obs-note">Placeholder values for the prototype. Keep the real key secret: anyone with it can stream to the club.{" "}
            <button type="button" className="ml-linkbtn" onClick={() => toast("New stream key made (prototype). Paste it into OBS again.")}>Reset key</button></p>
        </div>
      </div>
    </div>
  </section>;
}

/* ─── Locked (visitors and Thread Friends) ─── */

/** The club's shared locked card over a blurred still of class, with what membership adds. */
function LiveLocked({ stage, role, live, next }: { stage: Stage; role: Role; live: Live; next?: Live }) {
  const upcoming = stage === "ended" ? next : live;
  const hand = stage === "live" ? "a class is on right now!" : stage === "ended" ? "tonight’s replay is for members" : undefined;
  const when = upcoming ? (isToday(upcoming.start) ? "tonight at 7 PM MT" : `${shortDay(upcoming.start)} at 7 PM MT`) : undefined;
  const perks = ["Two live classes a month, 7 PM MT", "Chat and ask Chelsea questions live", "Every replay, with chapters", "Patterns and supply lists"];
  return <section className="ml-sec ml-locked" aria-labelledby="ml-locked-title">
    <div className="ml-wrap">
      <h2 id="ml-locked-title" className="mb-sr">Live classes are for members</h2>
      <div className="ml-lockbox">
        {stage === "live" && <span className="ml-live-badge ml-lockbox-badge"><i className="ml-dot" aria-hidden="true" /> Live · 214 sewing along</span>}
        <Locked need="members" what="live" hand={hand} image="/video/live-class.jpg"
          secondary={role === "friend" ? { label: "Back to your posts", to: M.posts } : { label: "Already a member? Sign in", to: M.account }}>
          <ul className="ml-perks">{perks.map(p => <li key={p}><Check size={16} strokeWidth={3} aria-hidden="true" /> {p}</li>)}</ul>
          {when && stage !== "live" && <p className="ml-lock-when">Next club class: <b>{when}</b></p>}
        </Locked>
      </div>
    </div>
  </section>;
}

/* ─── The page ─── */

function Body({ stage, setStage, startedAt, toast, live, next }: {
  stage: Stage; setStage: (s: Stage, fresh?: boolean) => void; startedAt: number; toast: Toast; live: Live; next?: Live;
}) {
  const role = useRole();
  const chelsea = role === "chelsea";
  const allowed = can(role, "live");
  const klass = classFor(live);
  const reduced = useReducedMotion();
  const clubs = upcomingLives(4, "club");

  const [msgs, setMsgs] = useState<Msg[]>(SEED_CHAT);
  const [qs, setQs] = useState<Question[]>(SEED_QS);
  const [pinned, setPinned] = useState<Pinned | null>(SEED_PIN);
  const [muted, setMuted] = useState<string[]>([]);
  const [viewers, setViewers] = useState(214);
  const [reactions, setReactions] = useState<Record<Reaction, number>>({ heart: 132, thread: 41, clap: 57 });
  const [floaters, setFloaters] = useState<{ id: number; kind: Reaction; x: number }[]>([]);
  const mutedRef = useRef(muted); mutedRef.current = muted;
  const startedRef = useRef(startedAt); startedRef.current = startedAt;
  const fid = useRef(0);

  const float = (kind: Reaction) => {
    setReactions(r => ({ ...r, [kind]: r[kind] + 1 }));
    if (reduced) return;
    const id = ++fid.current;
    setFloaters(f => [...f.slice(-14), { id, kind, x: Math.round(Math.random() * 60 - 30) }]);
    setTimeout(() => setFloaters(f => f.filter(x => x.id !== id)), 2200);
  };
  const floatRef = useRef(float); floatRef.current = float;

  // While live, the room keeps moving: new chat, viewers coming and going, hearts, votes.
  useEffect(() => {
    if (stage !== "live" || !allowed) return;
    let i = 0;
    const chat = setInterval(() => {
      const m = MORE_CHAT[i++ % MORE_CHAT.length];
      if (mutedRef.current.includes(m.by.id)) return;
      setMsgs(ms => [...ms, { id: `sim${Date.now()}`, by: m.by, text: m.text, t: clockAt(startedRef.current) }].slice(-150));
    }, 6500);
    const view = setInterval(() => setViewers(v => Math.max(150, v + Math.round(Math.random() * 9 - 3))), 4000);
    const hearts = setInterval(() => floatRef.current((["heart", "heart", "thread", "clap"] as const)[Math.floor(Math.random() * 4)]), 2800);
    const votes = setInterval(() => setQs(list => {
      const open = list.filter(q => q.status === "open" && q.by.id !== ME.id);
      const pick = open[Math.floor(Math.random() * open.length)];
      return pick ? list.map(q => q.id === pick.id ? { ...q, votes: q.votes + 1 } : q) : list;
    }), 9000);
    return () => { clearInterval(chat); clearInterval(view); clearInterval(hearts); clearInterval(votes); };
  }, [stage, allowed]);

  const room: RoomState = { msgs, setMsgs, qs, setQs, pinned, setPinned, muted, setMuted, viewers, startedAt, toast };
  const end = () => { setStage("ended"); toast("Class ended. The replay is processing."); };
  // While live, Chelsea's chat and questions come first; the studio (OBS, health) sits below.
  const studio = <Part p="club"><Studio stage={stage} startedAt={startedAt} room={room} toast={toast} onEnd={end}
    onStart={() => { setStage("live", true); toast("You’re live! Members can see you now."); }}
    onReset={() => setStage("before")} /></Part>;

  if (!allowed) return <>
    <Part p="club"><LiveLocked stage={stage} role={role} live={live} next={next} /></Part>
    <Part p="making"><Schedule role={role} stage={stage} current={live} /></Part>
  </>;

  return <>
    {chelsea && stage === "before" && studio}
    {stage === "before" && <Part p="club"><Making klass={klass} clubs={clubs} role={role} /></Part>}
    {stage === "before" && <Part p="club"><Ready role={role} qs={qs} setQs={setQs} toast={toast} /></Part>}
    {stage === "ended" && <Part p="club"><Ended klass={klass} next={next} role={role} room={room} toast={toast} /></Part>}
    {stage === "live" && <Part p="club"><LiveRoom room={room} klass={klass} chelsea={chelsea} role={role} reactions={reactions} react={float} floaters={floaters} onEnd={end} /></Part>}
    {chelsea && stage !== "before" && studio}
    {stage !== "live" && <Part p="making"><Schedule role={role} stage={stage} current={live} /></Part>}
  </>;
}

const readStage = (): Stage => {
  const s = new URLSearchParams(window.location.search).get("state");
  return s === "live" || s === "ended" ? s : "before";
};

export function MembersLive() {
  const [stage, setStageRaw] = useState<Stage>(readStage);
  // Jumping straight to "Live now" lands mid-class; pressing Start class starts the clock at zero.
  const [startedAt, setStartedAt] = useState(() => Date.now() - 21 * 6e4);
  const [reminded, setReminded] = useState(false);
  const [toastMsg, setToastMsg] = useState<{ id: number; text: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const toast: Toast = text => {
    setToastMsg({ id: Date.now(), text });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToastMsg(null), 3200);
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const setStage = (s: Stage, fresh = false) => {
    if (s === "live") setStartedAt(Date.now() - (fresh ? 0 : 21 * 6e4));
    setStageRaw(s);
    const url = new URL(window.location.href);
    url.searchParams.set("state", s);
    window.history.replaceState(window.history.state, "", url);
  };

  // Tonight's class (or the next one), and the one after it.
  const [live, next] = upcomingLives(2, "club");
  const klass = classFor(live);

  return <MotionConfig reducedMotion="user"><MembersPage page="live" header={<Header stage={stage} live={live} klass={klass} reminded={reminded} setReminded={setReminded} toast={toast} />}>
    <StateSwitch stage={stage} set={s => setStage(s)} />
    <Body stage={stage} setStage={setStage} startedAt={startedAt} toast={toast} live={live} next={next} />
    <div className="ml-toast-wrap" role="status" aria-live="polite">
      <AnimatePresence>
        {toastMsg && <motion.p key={toastMsg.id} className="ml-toast" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}>
          <Check size={16} strokeWidth={3} aria-hidden="true" /> {toastMsg.text}
        </motion.p>}
      </AnimatePresence>
    </div>
  </MembersPage></MotionConfig>;
}
